import React, { useEffect, useRef, useState } from 'react'
import {
  Circle,
  Designer,
  Image,
  Path,
  Rect,
  Text
} from '@looop/react-designer'
import ReactDOMServer from 'react-dom/server'
import SVGRenderer from '@looop/react-designer/lib/SVGRenderer'
import { t, Trans } from '@lingui/macro'
import { Box, Grid } from '@chakra-ui/react'

import {
  LooopS3BucketEnum,
  OrientationEnum,
  SvgObject
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import IconActionUndo from 'front-end/src/components/common/icon/IconActionUndo'
import { LooopS3FileUpload } from 'front-end/src/app/util/LooopS3FileUpload'

import IconNote from '../../../components/common/icon/IconNote'
import { useUpdateCertificatesMutation } from './CertificateTemplateDesigner.codegen'
import { useCertificateTemplateQuery } from './CertificateTemplatesPage.codegen'
import { notification } from '../../../components/common/notifications/Notifications'
import { HeadTitle } from '../../../components/common/head/HeadTitle'
import {
  ButtonDark,
  ButtonLight,
  ButtonPrimary
} from 'front-end/src/components'
import ElementFile from 'public/assets/img/element-file.svg'
import IconPicture from '../../../components/common/icon/IconPicture'

import './CertificateTemplateDesigner.less'

const objectTypes = {
  text: Text,
  rectangle: Rect,
  circle: Circle,
  polygon: Path,
  image: Image
}

const HORIZONTAL_ORIENTATION = {
  width: 594,
  height: 420
}

const VERTICAL_ORIENTATION = {
  width: 420,
  height: 594
}

const SQUARE_ORIENTATION = {
  width: 594,
  height: 594
}
// TODO fix in react-designer to always use numbers in it's objects
const formatSvgDimensionToNumber = (maybeNumber) => {
  if (maybeNumber === null || maybeNumber === undefined) return maybeNumber
  if (maybeNumber < 1) {
    notification.error(t`Dimensions of elements must be greater than 0`)
    throw new Error('Dimensions of elements must be greater than 0')
  }
  if (isNaN(maybeNumber)) {
    notification.error(t`Dimensions must contain only numerical values`)
    throw new Error('Dimensions must contain only numerical values')
  }
  return Number(maybeNumber)
}

const formatSvgPositionToNumber = (maybeNumber) => {
  if (isNaN(maybeNumber)) {
    notification.error(t`Position must only contain numerical values`)
    throw new Error('Position must only contain numerical values')
  }
  return Number(maybeNumber)
}

const formatSvgRotationToNumber = (maybeNumber) => {
  if (maybeNumber === null || maybeNumber === undefined) return maybeNumber
  if (isNaN(maybeNumber)) {
    notification.error(t`Rotation must only contain numerical values`)
    throw new Error('Rotation must only contain numerical values')
  }
  return Number(maybeNumber)
}

export const CertificateTemplateDesigner: React.FC<{ id: number }> = ({
  id
}) => {
  const [size, setSize] = useState({
    width: 0,
    height: 0
  })

  const { data } = useCertificateTemplateQuery({
    variables: {
      id
    }
  })

  const [objects, setObjects] = useState<any[]>([])
  const [savedObjects, setSavedObjects] = useState<any[]>([])

  useEffect(() => {
    const svgObjects =
      data?.admin?.organisation?.certificateTemplate?.svg_objects

    svgObjects && setObjects(svgObjects)
    svgObjects && setSavedObjects(svgObjects)

    switch (data?.admin?.organisation?.certificateTemplate?.orientation) {
      case OrientationEnum.HORIZONTAL:
        setSize(HORIZONTAL_ORIENTATION)
        break
      case OrientationEnum.VERTICAL:
        setSize(VERTICAL_ORIENTATION)
        break
      case OrientationEnum.SQUARE:
        setSize(SQUARE_ORIENTATION)
        break
      default:
        setSize(HORIZONTAL_ORIENTATION)
        break
    }
  }, [data])

  const synced = JSON.stringify(savedObjects) === JSON.stringify(objects)

  const [updateFn] = useUpdateCertificatesMutation()

  const designerEl = useRef(null)

  const generateSVG = (objects: SvgObject[]) => {
    const svgString = ReactDOMServer.renderToString(
      <SVGRenderer
        background="white"
        objects={objects}
        canvas={{ width: size.width, height: size.height }}
        objectTypes={objectTypes}
      />
    )

    return fixSVGforPdfGenerator(svgString)
  }

  const saveChanges = async (objects: SvgObject[]) => {
    const fixedObjects = objects.map((obj) => {
      return {
        ...obj,
        x: formatSvgPositionToNumber(obj.x),
        y: formatSvgPositionToNumber(obj.y),
        width: formatSvgDimensionToNumber(obj.width),
        height: formatSvgDimensionToNumber(obj.height),
        rotate: formatSvgRotationToNumber(obj.rotate),
        fontSize: obj.fontSize !== '' ? obj.fontSize : '15' // If user intentionally deleted font size, we will set back 15 px.
      }
    })

    await updateFn({
      variables: {
        id: id,
        objects: fixedObjects,
        svg: generateSVG(fixedObjects)
      }
    })
    setSavedObjects(objects)
  }
  const discardChanges = async () => {
    if (
      confirm(
        t`Are you sure you want to discard all of the changes since the last save?`
      )
    ) {
      setObjects(savedObjects)
    }
  }

  const insertText = (text) => {
    const updatedObjects = [
      ...objects,
      {
        type: 'text',
        x: size.width / 2,
        y: size.height / 2,
        text,
        fontFamily: 'Helvetica Neue',
        fontSize: '15'
      }
    ]
    setObjects(updatedObjects)
    updateFn({
      variables: {
        id: id,
        objects: updatedObjects,
        svg: generateSVG(objects)
      }
    })
  }

  return (
    <div className="CertificateTemplateDesigner">
      <HeadTitle
        title={t`Design certificate ${data?.admin?.organisation?.certificateTemplate?.name}`}
      />
      <header>
        <span className="CertificateTemplateDesigner__headTitle">
          <Trans>Insert into canvas</Trans>
        </span>

        <Box className="CertificateTemplateDesigner__buttonWrapper">
          <ButtonDark
            onClick={() => {
              insertText('{{organisation}}')
            }}
          >
            <Trans>Organisation</Trans>
          </ButtonDark>
          <ButtonDark
            onClick={() => {
              insertText('{{fullName}}')
            }}
          >
            <Trans>User name</Trans>
          </ButtonDark>
          <ButtonDark
            onClick={() => {
              insertText('{{firstName}}')
            }}
          >
            <Trans>User first name</Trans>
          </ButtonDark>
          <ButtonDark
            onClick={() => {
              insertText('{{lastName}}')
            }}
          >
            <Trans>User last name</Trans>
          </ButtonDark>
          <ButtonDark
            onClick={() => {
              insertText('{{firstLastName}}')
            }}
          >
            <Trans>First name + Last name</Trans>
          </ButtonDark>
          <ButtonDark
            onClick={() => {
              insertText('{{email}}')
            }}
          >
            <Trans>Email</Trans>
          </ButtonDark>
          <ButtonDark
            onClick={() => {
              insertText('{{title}}')
            }}
          >
            <Trans>Title of source</Trans>
          </ButtonDark>
          <ButtonDark
            onClick={() => {
              insertText('{{authorOfSource}}')
            }}
          >
            <Trans>Author of source</Trans>
          </ButtonDark>
          <ButtonDark
            onClick={() => {
              insertText('{{awardedDate}}')
            }}
          >
            <Trans>Awarded date</Trans>
          </ButtonDark>
          <ButtonDark
            onClick={() => {
              insertText('{{awardedDateLong}}')
            }}
          >
            <Trans>Awarded date - long</Trans>
          </ButtonDark>
          <ButtonDark
            onClick={() => {
              insertText('{{expiryDate}}')
            }}
          >
            <Trans>Expiry date</Trans>
          </ButtonDark>
          <ButtonDark
            onClick={() => {
              insertText('{{expiryDateLong}}')
            }}
          >
            <Trans>Expiry date - long</Trans>
          </ButtonDark>
          <ButtonDark
            onClick={() => {
              insertText(t`Placeholder...`)
            }}
            leftIcon={<IconNote />}
          >
            <Trans>Custom text</Trans>
          </ButtonDark>
          <ButtonDark
            onClick={() => {
              // TODO use a file choose dialog and upload the file to S3
              setObjects([
                ...objects,
                {
                  height: 100,
                  type: 'image',
                  width: 100,
                  x: size.width / 2,
                  y: size.height / 2,
                  xlinkHref: ElementFile // TODO set an actual image when uploaded to S3
                }
              ])
            }}
            leftIcon={<IconPicture />}
          >
            <Trans>Image</Trans>
          </ButtonDark>
        </Box>
      </header>
      <div className="CertificateTemplateDesigner__wrapper">
        <Grid gap="4px" position="absolute" right="20px" zIndex={100}>
          <ButtonLight
            size="sm"
            isFullWidth
            spacingDisabled
            disabled={synced}
            onClick={() => discardChanges()}
            leftIcon={<IconActionUndo />}
          >
            <Trans>Discard changes</Trans>
          </ButtonLight>
          <ButtonPrimary
            isFullWidth
            spacingDisabled
            disabled={synced}
            onClick={() => saveChanges(objects)}
          >
            <Trans>Save changes</Trans>
          </ButtonPrimary>
        </Grid>
        <Designer
          ref={designerEl}
          width={size.width}
          height={size.height}
          background="white"
          insertMenu={null}
          objectTypes={{
            text: Text,
            image: Image
          }}
          onUpdate={(objects) => {
            setObjects(objects)
          }}
          objects={objects}
          onImageDrop={async (file) => {
            notification.info(t`Image is uploading...`)

            return LooopS3FileUpload({
              S3UploadProps: {
                filename: file.name,
                S3path: `certificate-template-images/template-${id}`,
                bucket: LooopS3BucketEnum.PRIVATE
              },
              blob: file
            })
          }}
        />
      </div>
    </div>
  )
}

export const fixSVGforPdfGenerator = (svgString: string) => {
  // React-designer is not adding these two attributes into the SVG root element, so we have to add it here.
  const xmlnsPattern = /xmlns\s*=/
  const xlinkPattern = /xmlns:xlink\s*=/

  if (!xmlnsPattern.test(svgString)) {
    svgString = svgString.replace(
      '<svg',
      '<svg xmlns="http://www.w3.org/2000/svg"'
    )
  }

  if (!xlinkPattern.test(svgString)) {
    svgString = svgString.replace(
      '<svg',
      '<svg xmlns:xlink="http://www.w3.org/1999/xlink"'
    )
  }

  const parser = new DOMParser()
  const svgDoc = parser.parseFromString(svgString, 'image/svg+xml')

  const elements = Array.from(svgDoc.getElementsByTagName('*'))
  elements
    .filter((element) => element.hasAttribute('text-anchor'))
    .forEach((element) => element.removeAttribute('text-anchor'))

  // Setting default font size to elements that lack font size.
  svgDoc.querySelectorAll('text').forEach((textElement: SVGTextElement) => {
    if (
      !textElement.getAttribute('font-size') ||
      textElement.getAttribute('font-size')?.trim() === ''
    ) {
      textElement.setAttribute('font-size', '15px')
    }
  })

  return new XMLSerializer().serializeToString(svgDoc)
}

export default CertificateTemplateDesigner
